import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { AppState } from '../store/app.state';
import { selectCartItems } from '../store/cart/cart.selectors';
import { removeItem } from '../store/cart/cart.actions';
import { CartItem } from '../store/cart/cart.state';

import { UManagerSlugService } from './umanager-slug.service';
import { UtmPreservationService } from './utm-preservation.service';

/**
 * Enforces cart scope based on UTM slug channel restrictions.
 *
 * Problem addressed:
 * - When a channel slug (via utm_source) filters products/categories, the cart must not contain
 *   products outside the channel's allowed offersUuid set.
 */
@Injectable({
  providedIn: 'root',
})
export class CartChannelScopeService {
  private readonly destroy$ = new Subject<void>();

  private readonly store = inject(Store<AppState>);
  private readonly router = inject(Router);
  private readonly umanagerSlugService = inject(UManagerSlugService);
  private readonly utmPreservationService = inject(UtmPreservationService);

  private allowedProductUuids: Set<string> | null = null;
  private previousUtmSource: string | null = null;
  private initialized = false;
  private platformId = inject(PLATFORM_ID);


  initialize(): void {
    if (!isPlatformBrowser(this.platformId) || this.initialized) return;
    this.initialized = true;

    // Keep cart clean even if items are injected later (e.g. via URL direct add).
    this.store.select(selectCartItems).pipe(
      map(items => this.cartFingerprint(items)),
      distinctUntilChanged(),
      debounceTime(50),
      takeUntil(this.destroy$)
    ).subscribe(() => this.reconcileCartWithAllowedSet());

    // Detect UTM(slug) changes across navigations and refresh allowed set.
    this.previousUtmSource = this.getCurrentUtmSource();
    this.refreshAllowedSetFromCurrentUtm();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      debounceTime(50),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      const currentUtm = this.getCurrentUtmSource();
      if (currentUtm !== this.previousUtmSource) {
        this.previousUtmSource = currentUtm;
        this.refreshAllowedSetFromCurrentUtm();
      }
    });
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.allowedProductUuids = null;
    this.initialized = false;
  }

  private refreshAllowedSetFromCurrentUtm(): void {
    const utmSource = this.getCurrentUtmSource();

    // No UTM scope -> no filtering.
    if (!utmSource) {
      this.allowedProductUuids = null;
      return;
    }

    this.umanagerSlugService.checkSlugExists(utmSource).pipe(
      take(1),
      switchMap(existsResp => {
        if (!existsResp?.exists) {
          return of(null);
        }
        return this.umanagerSlugService.getChannelBySlug(utmSource).pipe(take(1));
      }),
      tap(channelData => {
        if (channelData?.status && channelData.status !== 'ACTIVE') {
          this.allowedProductUuids = null;
          this.utmPreservationService.disableUtmAndCleanUrl();
          return;
        }

        const offers = channelData?.offersUuid || [];
        this.allowedProductUuids = offers.length > 0 ? new Set(offers) : null;
      }),
      tap(() => this.reconcileCartWithAllowedSet()),
      catchError(err => {
        console.error('[CartChannelScope] Failed to load channel data for cart scope:', err);
        // Fail open (do not block cart) if channel lookup fails.
        this.allowedProductUuids = null;
        return of(null);
      })
    ).subscribe();
  }

  private reconcileCartWithAllowedSet(): void {
    if (!this.allowedProductUuids || this.allowedProductUuids.size === 0) return;

    this.store.select(selectCartItems).pipe(take(1)).subscribe(items => {
      const invalidItemIds = this.getInvalidItemIds(items, this.allowedProductUuids!);
      if (invalidItemIds.length === 0) return;

      // Remove invalid products from cart (not available for current slug/channel).
      invalidItemIds.forEach(itemId => this.store.dispatch(removeItem({ itemId })));
    });
  }

  private getInvalidItemIds(items: CartItem[], allowed: Set<string>): string[] {
    return (items || [])
      .filter(item => {

        const product = item?.product;
        const productUuid = product?.uuid;
        
        if (product?.productType === 'MIND') {
          return false;
        }
        if (!productUuid) return false;

        return !allowed.has(productUuid as string);
      })
      .map(item => item.id);
  }

  private cartFingerprint(items: CartItem[]): string {
    if (!items || items.length === 0) return '';
    // Stable snapshot to detect cart changes without deep equality.
    return items
      .map(i => `${i.id}:${i.product?.uuid || ''}:${i.selectedVariation?.uuid || ''}:${i.quantity}`)
      .sort()
      .join('|');
  }

  private getCurrentUtmSource(): string | null {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const utmFromUrl = (urlParams.get('utm_source') || '').trim();
      if (utmFromUrl) return utmFromUrl;

      return this.utmPreservationService.isUtmEnabled()
        ? (this.utmPreservationService.preservedUtmParams?.['utm_source'] || null)
        : null;
    } catch {
      return null;
    }
  }
}


